// Central definitions for status enums.
//
// Each enum is declared once here as:
//   - a `*_STATUSES` tuple   → feeds `z.enum(...)` in forms and the DB schema's type
//   - a `*_STATUS_OPTIONS`    → feeds `EnumSelect` (value + human label)
//
// Keeping both in one place means a new status is added in exactly one spot and
// every form, filter, and create dialog picks it up.

export interface EnumOption {
  value: string;
  label: string;
}

// --- S3_FEATURE_START ---
// ── File visibility (src/db/schema/files.ts) ──
//
// Storage is ALWAYS private. A `public` file is one the app chooses to serve at
// its stable `/f/:id/:name` URL (a redirect to a short-lived signed link) — the
// bucket itself never becomes public. `private` is the default for every file.
export const FILE_VISIBILITIES = ["private", "public"] as const;
export type FileVisibility = (typeof FILE_VISIBILITIES)[number];
// No `FILE_VISIBILITY_OPTIONS` yet: no screen offers the choice as a select.
// Add one here (value + label) the day one does.
// --- S3_FEATURE_END ---

// ── Audit log ──
//
// `action` is the `<entity>.<verb>` string stored on every audit_log row, and
// `entityType` is the coarse kind it groups under. Both are validated against
// these allowlists in `recordAudit` (src/lib/audit.ts) and drive the filter
// dropdowns in the admin viewer. Add a new action here when you audit a new
// mutation — the helper rejects anything not listed (see .claude/rules/audit.md).

export const AUDIT_ENTITY_TYPES = [
  "warehouse",
  "time_entry",
  "time_favorite",
  "time_signoff",
  "calendar",
  "calendar_rule",
  "calendar_event",
  "pto_request",
  "expense_claim",
  "app_menu",
  "person_link",
  "sign_in_alias",
  // --- S3_FEATURE_START ---
  "file",
  // --- S3_FEATURE_END ---
  "user",
  "auth",
] as const;
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number];

export const AUDIT_ACTIONS = [
  // A write to the NymblWarehouse through the gateway (src/lib/warehouse-route.ts) whose
  // route staged no more specific audit row.
  "warehouse.write",
  // Time Entry writes (src/api/time-routes.ts). Rows carry ids, dates and the period id only,
  // never comment text or hours.
  "time_entry.created",
  "time_entry.updated",
  "time_entry.deleted",
  "time_favorite.set",
  // A whole project starred or unstarred (this app's database; DECISIONS 2026-10-26).
  "time_project_favorite.set",
  "time_signoff.ensured",
  "time_signoff.submitted",
  "time_signoff.revoked",
  // Calendar suggestions (src/api/calendar-*.ts, src/lib/auth.ts). Rows carry ids, kinds,
  // normalized patterns and dates; never attendee addresses, never tokens.
  "calendar.connected",
  "calendar.disconnected",
  "calendar_rule.created",
  "calendar_rule.updated",
  "calendar_rule.deleted",
  "calendar_event.accepted",
  "calendar_event.dismissed",
  "calendar_event.restored",
  "calendar_event.accept_undone",
  // PTO decisions written through the gateway (src/api/team-routes.ts, shared with PTO).
  // Rows carry ids, status and booked days only, never the reason.
  "pto_request.approved",
  "pto_request.rejected",
  "pto_request.booked",
  // PTO requests written through the gateway (src/api/pto-routes.ts).
  "pto_request.created",
  "pto_request.updated",
  "pto_request.deleted",
  "pto_request.cancel_asked",
  "pto_request.kept",
  // My Expenses: ids and status only, never a description, amount, note or address.
  "expense_claim.created",
  "expense_claim.updated",
  "expense_claim.submitted",
  "expense_claim.approved",
  "expense_claim.rejected",
  "expense_claim.deleted",
  "expense_receipt.added",
  "expense_receipt.removed",
  "expense_receipt.scanned",
  // A run of the PTO allowance job that added rows (src/api/job-bodies.ts): the slot and the count.
  "pto_allowance.seeded",
  // A person's choice of which other Nymbl apps show in the left menu (src/api/app-menu.ts).
  "app_menu.updated",
  // Matching a sign-in to its warehouse Person (src/lib/person-links.ts): a second address
  // Microsoft vouched for, and an admin's link from Settings → Users. Ids, emails and names only.
  "sign_in_alias.added",
  "sign_in_alias.removed",
  "person_link.set",
  "person_link.removed",
  // --- S3_FEATURE_START ---
  // A file made public or private again (`PATCH /api/files/:id/visibility`).
  "file.visibility_changed",
  // --- S3_FEATURE_END ---
  // Authentication events (captured via Better Auth hooks).
  "auth.login",
  "auth.login_failed",
  "auth.logout",
  "auth.signup",
  "auth.password_reset_requested",
  "auth.password_reset_completed",
  // Admin user management (Better Auth admin plugin endpoints).
  "user.created",
  "user.deleted",
  "user.role_changed",
  "user.banned",
  "user.unbanned",
  "user.password_reset",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];
export const AUDIT_ACTION_OPTIONS: readonly EnumOption[] = AUDIT_ACTIONS.map((value) => ({
  value,
  label: value,
}));

// ── Time entry types (warehouse TimeEntryType.Enum) ──
//
// The chip on every grid row and entry. The five non-productive warehouse types (vacation,
// sick, holiday, unpaid, other) all show as "pto". "presales" exists in the warehouse but no
// rule assigns it today (see docs/plans/2026-10-15-time-entry.md, open questions).
/** @public */
export const TIME_ENTRY_TYPES = ["billable", "nonbillable", "internal", "presales", "pto"] as const;
export type TimeEntryType = (typeof TIME_ENTRY_TYPES)[number];
/** @public */
export const TIME_ENTRY_TYPE_OPTIONS: readonly EnumOption[] = [
  { value: "billable", label: "Billable" },
  { value: "nonbillable", label: "Non-billable" },
  { value: "internal", label: "Internal" },
  { value: "presales", label: "Presales" },
  { value: "pto", label: "PTO" },
];

// ── Calendar suggestions ──
export const CALENDAR_RULE_KINDS = ["series", "title_exact", "title_contains", "domain"] as const;
export type CalendarRuleKind = (typeof CALENDAR_RULE_KINDS)[number];
/** Kinds a person can type; a series id cannot be typed. */
export const MANUAL_CALENDAR_RULE_KINDS = ["title_exact", "title_contains", "domain"] as const;
export type ManualCalendarRuleKind = (typeof MANUAL_CALENDAR_RULE_KINDS)[number];
export const CALENDAR_RULE_KIND_LABELS: Record<CalendarRuleKind, string> = {
  series: "Same meeting series",
  title_exact: "Title is",
  title_contains: "Title contains",
  domain: "Attendee domain",
};
export const MANUAL_CALENDAR_RULE_KIND_OPTIONS: readonly EnumOption[] =
  MANUAL_CALENDAR_RULE_KINDS.map((value) => ({ value, label: CALENDAR_RULE_KIND_LABELS[value] }));
export const CALENDAR_RULE_SOURCES = ["learned", "manual"] as const;
export type CalendarRuleSource = (typeof CALENDAR_RULE_SOURCES)[number];
export const CALENDAR_RULE_SOURCE_LABELS: Record<CalendarRuleSource, string> = {
  learned: "Learned",
  manual: "Yours",
};
export const CALENDAR_EVENT_STATUSES = ["accepted", "dismissed"] as const;

// --- My Team: PTO requests panel tabs (DECISIONS 2026-10-15) ---
/** @public */
export const PTO_TABS = ["pending", "upcoming", "past"] as const;
export type PtoTab = (typeof PTO_TABS)[number];
/** @public */
export const PTO_TAB_LABELS: Record<PtoTab, string> = {
  pending: "To approve",
  upcoming: "Upcoming",
  past: "Past",
};

/** My Team tables: filter people by the tags the page exists to surface. "" = All. */
export const TEAM_TAG_FILTERS = ["below-target", "under-planned"] as const;
export type TeamTagFilter = "" | (typeof TEAM_TAG_FILTERS)[number];
export const TEAM_TAG_FILTER_OPTIONS: readonly EnumOption[] = [
  { value: "below-target", label: "Below target" },
  { value: "under-planned", label: "Under guidance" },
];

// --- PTO page (spec §1) ---
export const PTO_REQUEST_VIEWS = ["upcoming", "past"] as const;
/** Named PtoListView so it never clashes with My Team's PtoRequestView interface. */
export type PtoListView = (typeof PTO_REQUEST_VIEWS)[number];
export const PTO_REQUEST_VIEW_LABELS: Record<PtoListView, string> = {
  upcoming: "Upcoming",
  past: "Past",
};

export const PTO_PAGE_TABS = ["mine", "approvals"] as const;
export type PtoPageTab = (typeof PTO_PAGE_TABS)[number];

/** The step an approver's email button opens (`?decide=<id>&action=`), confirmed in the app. */
export const PTO_EMAIL_ACTIONS = ["approve", "reject"] as const;
export type PtoEmailAction = (typeof PTO_EMAIL_ACTIONS)[number];
export const EXPENSE_PAGE_TABS = ["mine", "approvals"] as const;
export type ExpensePageTab = (typeof EXPENSE_PAGE_TABS)[number];
export const EXPENSE_PAGE_TAB_LABELS: Record<ExpensePageTab, string> = {
  mine: "My expenses",
  approvals: "To approve",
};

export const PTO_PAGE_TAB_LABELS: Record<PtoPageTab, string> = {
  mine: "My time off",
  approvals: "Approvals",
};

// Profile page tabs (?tab=calendar); Profile is the default and leaves the param out.
export const PROFILE_PAGE_TABS = ["profile", "calendar"] as const;
export type ProfilePageTab = (typeof PROFILE_PAGE_TABS)[number];
export const PROFILE_PAGE_TAB_LABELS: Record<ProfilePageTab, string> = {
  profile: "Profile",
  calendar: "Calendar",
};

export const PTO_DAY_LENGTHS = ["full", "half", "hours"] as const;
export type PtoDayLength = (typeof PTO_DAY_LENGTHS)[number];
export const PTO_DAY_LENGTH_LABELS: Record<PtoDayLength, string> = {
  full: "Full day",
  half: "Half day",
  hours: "Hours",
};

export const PTO_APPROVAL_SCOPES = ["mine", "all"] as const;
export type PtoApprovalScope = (typeof PTO_APPROVAL_SCOPES)[number];

/**
 * The outbox `metadata.source` of a PTO email: "pto.decision" for an approver's decision (approve,
 * reject, remove, keep; the default), "pto.request" for what a requester did (submit, edit,
 * withdraw, cancel, ask to cancel).
 */
const PTO_NOTICE_SOURCES = ["pto.decision", "pto.request"] as const;
export type PtoNoticeSource = (typeof PTO_NOTICE_SOURCES)[number];

// ── Scheduled jobs (src/db/schema/jobs.ts, src/lib/jobs/schedule.ts) ──
// A run's status as `job_run` stores it. `failed` is retried while attempts remain; `gave_up` is
// the last attempt failing. A mode is how the job runs now (`jobMode`).
export const JOB_RUN_STATUSES = ["running", "done", "failed", "gave_up", "skipped"] as const;
export const JOB_RUN_STATUS_OPTIONS: readonly EnumOption[] = [
  { value: "running", label: "Running" },
  { value: "done", label: "Done" },
  { value: "failed", label: "Failed" },
  { value: "gave_up", label: "Gave up" },
  { value: "skipped", label: "Skipped" },
];
export const JOB_MODE_OPTIONS: readonly EnumOption[] = [
  { value: "off", label: "Off" },
  { value: "dry-run", label: "Dry run" },
  { value: "live", label: "Live" },
];
// Settings, Jobs: the page's two tabs (`?view=history`).
export const JOBS_PAGE_VIEWS = ["jobs", "history"] as const;
export type JobsPageView = (typeof JOBS_PAGE_VIEWS)[number];
export const JOBS_PAGE_VIEW_LABELS: Record<JobsPageView, string> = {
  jobs: "Jobs",
  history: "Run history",
};
